import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { Card } from '@/components/ui/card/Card';
import { DropDownList } from '@/components/form/inputs/DropDownList';
import type { Machine } from '../../type/Machine';
import { MachineManagerModal } from '../MachineManagerModal';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { useTranslation } from '@/hooks/useTranslation';
import { Cpu, Settings2, RefreshCw } from 'lucide-react';
import { TraceabilityApiClient } from '../../api/TraceabilityApiClient';
import { useApiClient } from '@/hooks/useApiClient';
import { cn } from '@/lib/utils';

type MachineTabProps = {
  values: Record<string, unknown>;
  companyName: string;
  onChange: (name: string, value: unknown) => void;
};

export const MachineTab = forwardRef(function MachineTab(
  { values, companyName, onChange }: MachineTabProps,
  ref
) {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const machineRef = useRef<{ validate: () => boolean }>(null);
  const [showManager, setShowManager] = useState(false);
  const { t } = useTranslation();
  const http = useApiClient();

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  // Fetch machines from API (used for manual refresh button)
  const fetchMachines = async () => {
    if (!traceabilityApiClient) return;

    setIsLoading(true);

    try {
      const response = await traceabilityApiClient.machineReadAll({});

      if (response.ok && response.data?.machines) {
        const machineList = response.data.machines.map(
          (item: { machine: Machine }) => item.machine
        );
        setMachines(machineList);
      }
    } catch (error) {
      console.error('Failed to fetch machines:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load machines on mount with abort on unmount
  useEffect(() => {
    if (!traceabilityApiClient) return;

    const ac = new AbortController();
    setIsLoading(true);

    traceabilityApiClient
      .machineReadAll({}, { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        if (res.ok && res.data?.machines) {
          const machineList = res.data.machines.map((item: { machine: Machine }) => item.machine);
          setMachines(machineList);
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch machines:', err);
      })
      .finally(() => {
        if (!ac.signal.aborted) setIsLoading(false);
      });

    return () => ac.abort();
  }, [traceabilityApiClient]);

  const handleSelectMachine = (val: string | null) => {
    if (!val) {
      onChange('machineId', null);
      onChange('machine', null);
      return;
    }

    const asId = Number(val);
    const m =
      !isNaN(asId) && asId > 0
        ? machines.find((m) => m.id === asId)
        : machines.find((m) => m.name === val);

    if (m) {
      onChange('machineId', m.id ?? null);
      onChange('machine', m);
    }
  };

  const selectedMachineId = (values['machineId'] as number | null | undefined) ?? null;

  const selectedMachine = useMemo(
    () => (selectedMachineId !== null ? machines.find((m) => m.id === selectedMachineId) ?? null : null),
    [machines, selectedMachineId]
  );

  // Use machine id as the dropdown value when available (avoids duplicate-name conflicts)
  const selectedDropdownValue = selectedMachine
    ? selectedMachine.id !== undefined
      ? String(selectedMachine.id)
      : selectedMachine.name
    : '';

  // Clear machineId when the selected machine no longer exists in the list
  useEffect(() => {
    if (selectedMachineId !== null && !selectedMachine && machines.length > 0) {
      onChange('machineId', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMachineId, selectedMachine, machines]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      return machineRef.current?.validate() ?? true;
    },
  }));

  return (
    <div className="grid grid-cols-1 gap-5">
      <Card padding="sm">
        <div className="mb-4 space-y-1">
          <SectionLabel icon={Cpu}>
            {t('traceability.machineTab.sectionTitle')}
          </SectionLabel>
          <p className="text-sm text-muted-foreground">
            {t('traceability.machineTab.sectionDescription')}
          </p>
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <DropDownList
              ref={machineRef}
              name="machineName"
              label={t('traceability.machineTab.dropdownLabel')}
              value={selectedDropdownValue}
              onChange={(val) => handleSelectMachine(val)}
              options={machines.map((m) => ({
                label: m.name,
                value: m.id !== undefined ? String(m.id) : m.name,
                id: m.id,
              }))}
              placeholder={t('traceability.machineTab.dropdownPlaceholder')}
              required
            />
          </div>
          <div className="flex items-end pb-0.5">
            <IconButton
              icon={RefreshCw}
              label={t('traceability.machineTab.refreshList')}
              size="icon"
              onClick={() => fetchMachines()}
              loading={isLoading}
            />
          </div>
        </div>

        <div className="mt-4">
          <Button className="w-full" onClick={() => setShowManager(true)}>
            <Settings2 aria-hidden />
            {t('traceability.machineTab.openManager')}
          </Button>
        </div>

        {selectedMachine && (
          <Card
            as="dl"
            tier="muted"
            padding="none"
            className="mt-4 flex flex-wrap items-center gap-0 px-4 py-2.5"
          >
            <div className="flex flex-col border-r border-border/60 pr-4 mr-4">
              <dt className="text-xs text-muted-foreground">
                {t('traceability.machineTab.dropdownLabel')}
              </dt>
              <dd className="text-sm font-semibold text-primary">{selectedMachine.name}</dd>
            </div>
            <div className="flex flex-col border-r border-border/60 pr-4 mr-4">
              <dt className="text-xs text-muted-foreground">
                {t('traceability.machineTab.brandLabel')}
              </dt>
              <dd className="text-sm font-medium text-foreground">{selectedMachine.brand}</dd>
            </div>
            <div className="flex flex-col border-r border-border/60 pr-4 mr-4">
              <dt className="text-xs text-muted-foreground">
                {t('traceability.machineTab.modelLabel')}
              </dt>
              <dd className="text-sm font-medium text-foreground">{selectedMachine.model}</dd>
            </div>
            <div
              className={cn(
                'flex flex-col',
                selectedMachine.kafkaName && 'border-r border-border/60 pr-4 mr-4'
              )}
            >
              <dt className="text-xs text-muted-foreground">
                {t('traceability.machineTab.serialLabel')}
              </dt>
              <dd className="font-mono text-sm text-foreground">{selectedMachine.serialNumber}</dd>
            </div>
            {selectedMachine.kafkaName && (
              <div className="flex flex-col">
                <dt className="text-xs text-muted-foreground">
                  {t('traceability.machineTab.kafkaNameLabel')}
                </dt>
                <dd className="font-mono text-sm text-foreground">{selectedMachine.kafkaName}</dd>
              </div>
            )}
          </Card>
        )}
      </Card>

      <MachineManagerModal
        isOpen={showManager}
        companyName={companyName}
        onClose={() => setShowManager(false)}
        onSave={async (machine, action) => {
          // Refresh machines from API
          await fetchMachines();

          if (machine && action !== 'delete') {
            // Select by id when available, fall back to name
            handleSelectMachine(machine.id !== undefined ? String(machine.id) : machine.name);
          }

          if (
            machine &&
            action === 'delete' &&
            (machine.id !== undefined
              ? machine.id === selectedMachine?.id
              : machine.name === selectedMachine?.name)
          ) {
            onChange('machineId', null);
            onChange('machine', null);
          }
        }}
        selectedMachineName={selectedMachine?.name ?? ''}
      />
    </div>
  );
});
